import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { PageViews } from "@/components/layout/page-views";
import { Layout } from "@/components/layout/layout";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { Toaster } from "@/components/ui/sonner";
import { WorkspaceGate } from "@/components/auth/workspace-gate";
import { AuthProvider } from "@/lib/session/auth-context";
import { WorkspaceProvider } from "@/lib/session/workspace-context";
import {
  ConfirmEmailChangePage,
  ConfirmVerificationPage,
  ResetPasswordPage,
} from "@/routes/auth-confirm";
import { AssistantPage } from "@/routes/assistant";
import { BankingPage } from "@/routes/banking";
import { ClosePage } from "@/routes/close";
import { DashboardPage } from "@/routes/dashboard";
import { DocumentDetailPage } from "@/routes/document-detail";
import { DocumentsPage } from "@/routes/documents";
import { RootRedirect } from "@/routes/home";
import { InvitePage } from "@/routes/invite";
import { LegalPage } from "@/routes/legal/legal";
import { LoginPage } from "@/routes/login";
import { MailboxPage } from "@/routes/mailbox";
import { CapturePage } from "@/routes/capture";
import { ProfilePage } from "@/routes/profile";
import { NotFoundPage } from "@/routes/not-found";
import { ReportsPage } from "@/routes/reports";
import { AssetsPage } from "@/routes/assets";
import { TripsPage } from "@/routes/trips";
import { SettingsPage } from "@/routes/settings";
import { WorkspaceNewPage } from "@/routes/workspace-new";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WorkspaceProvider>
          <BrowserRouter>
            <PageViews />
            <Routes>
              <Route element={<Layout />}>
                <Route index element={<RootRedirect />} />
                <Route path="login" element={<LoginPage />} />
                {/* Handles its own sign-in redirect so the token survives the detour. */}
                <Route path="invite/:token" element={<InvitePage />} />
                <Route path="impressum" element={<LegalPage page="imprint" />} />
                <Route path="datenschutz" element={<LegalPage page="privacy" />} />
                <Route path="agb" element={<LegalPage page="terms" />} />
                <Route path="avv" element={<LegalPage page="dpa" />} />
                {/* The links in the auth emails (pb_hooks/lib/auth-mail.js, migration 1758000019). */}
                <Route
                  path="auth/confirm-verification/:token"
                  element={<ConfirmVerificationPage />}
                />
                <Route path="auth/confirm-password-reset/:token" element={<ResetPasswordPage />} />
                <Route
                  path="auth/confirm-email-change/:token"
                  element={<ConfirmEmailChangePage />}
                />
                <Route element={<ProtectedRoute />}>
                  <Route path="workspaces/new" element={<WorkspaceNewPage />} />
                  {/* About the person, not a business: reachable before the first one exists. */}
                  <Route path="profile" element={<ProfilePage />} />
                  <Route element={<WorkspaceGate />}>
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="documents" element={<DocumentsPage />} />
                    <Route path="documents/:id" element={<DocumentDetailPage />} />
                    <Route path="banking" element={<BankingPage />} />
                    <Route path="close" element={<ClosePage />} />
                    {/* Not /inbox: that prefix is proxied to services/inbox. */}
                    <Route path="mailbox" element={<MailboxPage />} />
                    <Route path="capture" element={<CapturePage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="assets" element={<AssetsPage />} />
                    <Route path="trips" element={<TripsPage />} />
                    <Route path="assistant/:conversationId?" element={<AssistantPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    {/* The plan's old address, still in bookmarks and older emails. */}
                    <Route path="plans" element={<Navigate to="/settings?tab=plan" replace />} />
                  </Route>
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
          {/* Bottom right: the header actions live in the top right corner. */}
          <Toaster richColors position="bottom-right" />
        </WorkspaceProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
